import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Body,
	Child,
	Container,
	StyledButton,
	StyledTextField,
	Subtitle,
	Title,
	VerificationCode,
} from '../components';
import { darkRed, primaryRed } from '../models/colors';
import { AuthService } from '../services';
import { login } from '../text';
import type { OnChangeEvent, OnSubmitEvent } from '../types';
import { isValidEmailFormat, parseQueryString } from '../utils';

interface ILoginUrlParams {
	ref: string;
	request: string;
	email: string;
	token: string;
}

export const Login = () => {
	const location = useLocation();
	const query = parseQueryString<ILoginUrlParams>(location.search, { ref: '', request: '', email: '', token: '' });
	const urlParamsRef = query && query.ref !== undefined ? query.ref : '';
	const urlParamsRequest = query && query.request !== undefined ? query.request : '';
	const email = query && query.email !== undefined ? query.email : '';
	const token = query && query.token !== undefined ? query.token : '';
	const pinRequest = urlParamsRequest === 'pin';
	return pinRequest ? <Pin params={{ urlParamsRef, urlParamsRequest, email, token }} /> : <InitalLogin />;
};

const InitalLogin = () => {
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [tokenV2, setTokenV2] = useState('');
	const [isHumanV2, setIsHumanV2] = useState(false);
	const [doesNotExist, setDoesNotExist] = useState(false);

	return (
		<Container container>
            <Child>
				<Title>{login.initialLogin.title}</Title>
			</Child>
            <Child>
				<Body variant={`body1`}>
					{login.initialLogin.body1}
					<br />
					<br />
					{login.initialLogin.body2}
					<br />
					<br />
					{login.initialLogin.body3}
				</Body>
			</Child>
            <Child style={{ maxWidth: '356px', marginTop: '36px' }}>
				<Body variant={`subtitle1`} style={{ padding: 0, marginBottom: '8px' }}>{`Not an existing user?`}</Body>
				<StyledButton fullWidth onClick={() => navigate(`/register`)}>
					Create a New Customer Profile
				</StyledButton>
			</Child>
            <Child style={{ maxWidth: '356px', marginTop: '72px' }}>
				<Body
					variant={`subtitle1`}
					style={{ padding: 0, marginBottom: '8px' }}
				>{`Already have a profile?`}</Body>
				<form onSubmit={(e: OnSubmitEvent) => e.preventDefault()}>
					<StyledTextField
						autoFocus
						value={email}
						name="email"
						label="Email Address"
						style={{ margin: 0 }}
						onChange={(e: OnChangeEvent) => setEmail(e.currentTarget.value)}
					/>
                    <Child container>
						<ReCAPTCHA
							sitekey={`6LeS1Z4UAAAAAA8YbI_o663eg166GUwB5Vgf2PzN`}
							onChange={(token: string | null) => {
								setTokenV2(token || '');
								setIsHumanV2(true);
							}}
						/>
					</Child>
                    <Child container>
						<Subtitle style={{ display: doesNotExist ? 'inline-flex' : 'none', color: darkRed }}>
							The email address you have entered does not match an existing user.
						</Subtitle>
						<StyledButton
							fullWidth
							onClick={() => {
								AuthService.requestLogin(email, tokenV2).then(
									() => navigate(`/login?request=pin`, { state: { email, token: tokenV2 } }),
									(err: any) => {
										if (err.status === 404) {
											setDoesNotExist(true);
										} else {
											Promise.reject(err);
										}
									}
								);
							}}
							disabled={!isValidEmailFormat(email) || !isHumanV2}
						>
							Login
						</StyledButton>
					</Child>
				</form>
			</Child>
		</Container>
	);
};

const Pin = ({
	params,
}: {
	params: { urlParamsRef: string; urlParamsRequest: string; email: string; token: string };
}) => {
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const email = (location.state as any)?.email || query.get('email') || '';
	const token = (location.state as any)?.token || query.get('token') || '';

	const navigate = useNavigate();
	const [pin, setPin] = useState('');
	const [valid, setValid] = useState(false);
	const [wrongPin, setWrongPin] = useState(false);
	const [resentPin, setResentPin] = useState(false);
	const [_, setDoesNotExist] = useState(false);

	return (
		<Container container>
            <Child>
				<Title>{login.pin.title}</Title>
                {params.urlParamsRef === '/register' ? <Subtitle>{login.pin.subtitle}</Subtitle> : null}
			</Child>
            <Child>
				<VerificationCode
					checkValidity={v => {
						setPin(v);
						setValid(v.length === 6);
					}}
					type="numeric"
					length={6}
				/>
			</Child>
            {wrongPin ? (
                <Child>
					<Subtitle style={{ color: primaryRed }}>
						You have entered an incorrect PIN. Please try again.
					</Subtitle>
				</Child>
            ) : null}
            {resentPin ? (
                <Child>
					<Subtitle style={{ color: primaryRed }}>A new PIN has been sent to your email address.</Subtitle>
				</Child>
            ) : null}
            <Child container>
				<StyledButton
					style={{ marginRight: '10pt' }}
					disabled={!valid}
					onClick={() =>
						AuthService.login(pin).then(
							_ => navigate('/'),
							err => {
								if (err.status === 400) {
									setWrongPin(true);
									setResentPin(false);
								}
							}
						)
					}
				>
					Login
				</StyledButton>
				<StyledButton
					onClick={() => {
						AuthService.requestLogin(email, token).then(
							() => {
								setWrongPin(false);
								setResentPin(true);
							},
							err => {
								if (err.status === 404) {
									setDoesNotExist(true);
								} else {
									Promise.reject(err);
								}
							}
						);
					}}
				>
					Resend PIN
				</StyledButton>
			</Child>
            <Child container>
				<Subtitle>{login.pin.resend}</Subtitle>
			</Child>
		</Container>
	);
};
